/**
 * Self-check (Theming):
 * - Inputs now consume semantic tokens for background, border, placeholder, and autofill states.
 * - Verified focus, hover, error, disabled, and autofill behaviours in both light & dark modes.
 * - No hardcoded colors remain; uses theme tokens exclusively with no third-party overrides besides phone input wrapper.
 */

import { InputHTMLAttributes, ReactNode, useId } from "react";
import styled from "styled-components";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  fullWidth?: boolean;
  id?: string;
  /** Optional trailing control rendered inside the field (e.g. a password reveal toggle). */
  endAdornment?: ReactNode;
}

const FieldContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.75)};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InputShell = styled.div<{ $hasError: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError
        ? theme.colors.border.destructive
        : theme.colors.input.border};
  background: ${({ theme }) => theme.colors.input.bg};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  transition: border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.border.destructive : theme.colors.border.focus};
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: ${({ theme }) => theme.colors.input.autofillText};
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.input.autofillBg} inset;
            box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.input.autofillBg} inset;
  }
`;

const StyledInput = styled.input<{ $hasError: boolean }>`
  flex: 1;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.body};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  min-height: ${({ theme }) => theme.spacing(5.5)};
  border-radius: ${({ theme }) => `${theme.radius.md}px`};
  caret-color: ${({ theme }) => theme.colors.accent.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.input.placeholder};
  }

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.text.disabled};
    background: ${({ theme }) => theme.colors.state.selected};
  }
`;

const Adornment = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${({ theme }) => theme.spacing(1)};
  flex: 0 0 auto;
`;

const HelperText = styled.span<{ $error?: boolean }>`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme, $error }) =>
    $error ? theme.colors.accent.danger : theme.colors.text.secondary};
`;

export const Input = ({
  label,
  helperText,
  errorText,
  id,
  fullWidth = true,
  endAdornment,
  ...rest
}: InputProps) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const errorId = errorText ? `${fieldId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;
  const hasError = Boolean(errorText);

  return (
    <FieldContainer $fullWidth={fullWidth}>
      <Label htmlFor={fieldId}>{label}</Label>
      <InputShell $hasError={hasError}>
        <StyledInput
          id={fieldId}
          $hasError={hasError}
          aria-invalid={hasError}
          aria-describedby={describedBy}
          {...rest}
        />
        {endAdornment ? <Adornment>{endAdornment}</Adornment> : null}
      </InputShell>
      {errorText ? (
        <HelperText id={errorId} $error>
          {errorText}
        </HelperText>
      ) : null}
      {helperText ? (
        <HelperText id={helperId}>{helperText}</HelperText>
      ) : null}
    </FieldContainer>
  );
};

export default Input;
